import React, { useEffect } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import styles from './fileEditModal.module.scss';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { File, FileType, User } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { useForm } from 'react-hook-form';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import trashCanIcon from 'v2/icons/trash-outlined.svg';
import { Button, Form } from 'react-bootstrap';
import FileTypeDropdown from '../FileTypeDropdown';
import FormModal from 'v2/common/FormModal/FormModal';
import { DATE_FORMAT } from 'utils/constants';
import { getUserFullName } from 'v2/utils';

type ModalsState = { edit: boolean; delete: boolean };

interface EditFileModalProps {
  file: File;
  show: ModalsState;
  handleShow: (state: ModalsState) => void;
  onUpdate?: () => void;
}

interface FileTypeAndDescriptionFields {
  description?: string;
  type?: FileType;
}

const EditFileModal = ({
  file,
  show,
  handleShow,
  onUpdate,
}: EditFileModalProps) => {
  const formContext = useForm<FileTypeAndDescriptionFields>({
    mode: 'onChange',
    defaultValues: {
      type: file?.type,
      description: file?.description,
    },
  });

  const { isLoading: isFileUpdateLoading, mutate: fileUpdate } = useMutation(
    async (data: FileTypeAndDescriptionFields) => {
      return await restFactory.patch<File>(
        `/dj/api/v1/file/${file?.id}/`,
        data
      );
    },
    {
      onSuccess: () => {
        toast.success('Changes saved successfully.');
        handleShow({ edit: false, delete: false });
        onUpdate?.();
      },
      onError: () => {
        toast.error("Changes couldn't be saved.");
      },
    }
  );

  useEffect(() => {
    if (show.edit) {
      formContext.setFocus('description');
    }
  }, [show.edit]);

  const handleOnSubmit = async (data: FileTypeAndDescriptionFields) => {
    if (formContext.formState.isValid) {
      fileUpdate(data);
    }
  };

  return (
    <>
      <FormModal
        title={file.name}
        onClose={onUpdate}
        onSubmit={handleOnSubmit}
        isLoading={isFileUpdateLoading}
        formContext={formContext}
        className={classnames(styles.viewModals)}
      >
        <div className={classnames(styles.fileOwner)}>
          {`Uploaded by ${getUserFullName(file.owner as User)} on ${moment(
            file.upload_time
          ).format(DATE_FORMAT.SHORT)}`}
        </div>
        <div>
          <FormLabel label={`File Type`} required={false} />
          <FileTypeDropdown />
        </div>
        <div>
          <FormLabel label={`File Description`} required={false} />
          <Form.Control
            as="textarea"
            placeholder="File Description"
            rows={4}
            autoComplete="off"
            {...formContext.register('description', { required: false })}
          />
        </div>
        <Button
          className={styles.deleteFileButton}
          onClick={() => handleShow({ edit: false, delete: true })}
          aria-label="removeFile"
          data-testid="remove-file"
        >
          <img src={trashCanIcon} alt="deleteAttachedFile"></img>
          <span className="ml-2 mt-1">Remove File</span>
        </Button>
      </FormModal>
    </>
  );
};

export default EditFileModal;
